import Twemoji from "react-twemoji";
import { ReactNode, FormEvent, useState, useRef, useEffect } from "react";
import { useOutletContext } from "react-router-dom";

import { ScopeType } from "sockapi/scope-types";
import { HintResponseType, HintStrength } from "sockapi/scope-specs/hint-spec";
import { TeamPuzzleSockAPIScopeState } from "sockapi/scope-specs/team-spec";
import {
  SubmitHintResultType,
  checkSubmitHint,
  HINT_CONFIG,
} from "sockapi/scope-specs/team-hints-spec";
import { useUILock } from "client-util/useUILock";
import { IconType, Icon } from "components/Icon";
import { Link, PuzzleLink } from "components/Link";
import Tooltip from "components/Tooltip";
import { Spinner } from "components/Loading";
import { Paper } from "components/Material";
import HintDisplay, {
  HINT_STRENGTH_ICON,
  HINT_STRENGTH_DESC,
  HINT_STRENGTH_TOOLTIP,
} from "components/PuzzlePage/HintDisplay";
import { useShowHints } from "components/PuzzlePage/PuzzlePageToolbar";
import { SpoilerText } from "components/PuzzleContent/puzzle-components";
import { makeTeamPagePath } from "components/TeamDisplayName";
import { useNavStore } from "stores/NavStore";
import { useSockClient, useTeamId } from "stores/ServerInteractionStore";
import {
  useScopeStateResolvable,
  useTeamScopeState,
  useIsPuzzleSolved,
  useNumTotalHintTokens,
  useNumUsedHintTokens,
  useSortedHintsForTeamPuzzle,
  useIsHuntOver,
  usePuzzleDisplayNames,
} from "stores/ScopeStateStore";
import settings from "settings";

import "./HintsDisplay.css";

export enum CannedHintDisplayType {
  OPENED = "opened",
  NEXT_TO_OPEN = "next_to_open",
  UNOPENED = "unopened",
  ADMIN_UNOPENED = "admin_unopened",
  ADMIN_LOCKED = "admin_locked",
}

export function CannedHintDisplay(props: {
  puzName: string;
  index: number;
  teamId: string;
  displayType: CannedHintDisplayType;
  spoiler: boolean;
}) {
  const { puzName, index, teamId, displayType, spoiler } = props;
  const isContentAvailable = [
    CannedHintDisplayType.OPENED,
    CannedHintDisplayType.ADMIN_UNOPENED,
    CannedHintDisplayType.ADMIN_LOCKED,
  ].includes(displayType);
  const cannedHintScopeState = useScopeStateResolvable(
    ScopeType.CANNED_HINT,
    isContentAvailable
      ? {
          puzName,
          index,
        }
      : null
  );
  const openCannedHintLock = useUILock();
  const sockClient = useSockClient();

  if (isContentAvailable && cannedHintScopeState === null)
    return <Spinner inline={true} />;

  const icon = ((): ReactNode => {
    switch (displayType) {
      case CannedHintDisplayType.OPENED:
        return <Icon icon={IconType.OPEN_CANNED_HINT} />;
      case CannedHintDisplayType.NEXT_TO_OPEN:
        return <Icon icon={IconType.CANNED_HINT} />;
      case CannedHintDisplayType.UNOPENED:
        return (
          <Icon icon={IconType.CANNED_HINT} className="hints-icon-disabled" />
        );
      case CannedHintDisplayType.ADMIN_UNOPENED:
        return (
          <Tooltip text="Unopened by team">
            <Icon icon={IconType.CANNED_HINT} className="hints-icon-disabled" />
          </Tooltip>
        );
      case CannedHintDisplayType.ADMIN_LOCKED:
        return (
          <Tooltip text="Not yet found by team">
            <Twemoji tag="span">🔒</Twemoji>
          </Tooltip>
        );
    }
  })();
  const isSeenByTeam = [CannedHintDisplayType.OPENED].includes(displayType);

  return (
    <div className="canned-hint">
      <div className="canned-hint-icon">{icon}</div>
      {cannedHintScopeState !== null ? (
        <div
          className={`canned-hint-content ${
            !isSeenByTeam ? "canned-hint-content-unopened" : ""
          }`}
        >
          {spoiler ? (
            <SpoilerText>{cannedHintScopeState.content}</SpoilerText>
          ) : (
            <>{cannedHintScopeState.content}</>
          )}
        </div>
      ) : displayType === CannedHintDisplayType.NEXT_TO_OPEN ? (
        <button
          disabled={openCannedHintLock.isHeld}
          onClick={() => {
            openCannedHintLock.acquireAndRun(async (getIsCanceled) => {
              await sockClient.sendScopeStepAndWaitAsync(
                {
                  type: ScopeType.TEAM_CANNED_HINTS,
                  teamId,
                },
                {
                  openCannedHint: {
                    puzName,
                    index,
                  },
                }
              );
            });
          }}
        >
          Open
        </button>
      ) : null}
    </div>
  );
}

// unused this year
export function CannedHintsDisplay(props: {
  puzData: TeamPuzzleSockAPIScopeState;
}) {
  const { puzData } = props;
  const { puzName } = puzData;
  const teamId = useTeamId();
  const teamCannedHintsScopeState = useScopeStateResolvable(
    ScopeType.TEAM_CANNED_HINTS,
    teamId === null ? null : { teamId }
  );

  if (teamId === null || teamCannedHintsScopeState === null)
    return <Spinner inline={true} />;

  const cannedHints = teamCannedHintsScopeState.cannedHints[puzName] ?? {
    numTotal: 0,
    numOpened: 0,
  };
  const hintWord = cannedHints.numTotal === 1 ? "hint" : "hints";

  if (!(cannedHints.numTotal > 0)) return null;

  return (
    <>
      <h1>Canned Hints</h1>
      <div>
        You’ve found{" "}
        <b>
          {cannedHints.numTotal} canned {hintWord}
        </b>
        ! You’ve opened <b>{cannedHints.numOpened} of them</b>. There is no
        penalty for opening these canned hints, but they can only be used on
        this puzzle.
      </div>
      {Array.from(Array(cannedHints.numTotal).keys()).map((i) => (
        <CannedHintDisplay
          key={i}
          puzName={puzName}
          index={i}
          teamId={teamId}
          displayType={
            i < cannedHints.numOpened
              ? CannedHintDisplayType.OPENED
              : i === cannedHints.numOpened
                ? CannedHintDisplayType.NEXT_TO_OPEN
                : CannedHintDisplayType.UNOPENED
          }
          spoiler={true}
        />
      ))}
      {cannedHints.numTotal === cannedHints.numMax ? (
        <div>You&rsquo;ve found all the canned hints for this puzzle!</div>
      ) : null}
    </>
  );
}

function CustomHintsDisplay(props: { puzData: TeamPuzzleSockAPIScopeState }) {
  const { puzData } = props;
  const { puzName } = puzData;
  const [isFollowup, setIsFollowup] = useState(false);
  const followupHintHeaderRef = useRef<HTMLHeadingElement>(null);
  const hintTextAreaRef = useRef<HTMLTextAreaElement>(null);

  const teamId = useTeamId();
  const teamScopeState = useTeamScopeState();
  const teamHintsScopeState = useScopeStateResolvable(
    ScopeType.TEAM_HINTS,
    teamId === null ? null : { teamId }
  );
  /*
  const teamCannedHintsScopeState = useScopeStateResolvable(
    ScopeType.TEAM_CANNED_HINTS,
    teamId === null ? null : { teamId }
  );
  */
  const numTotalHintTokens = useNumTotalHintTokens(teamId);
  const numUsedHintTokens = useNumUsedHintTokens(teamId);
  const sortedHints = useSortedHintsForTeamPuzzle(teamId, puzName, true);
  const isSolved = useIsPuzzleSolved(puzName);
  const sockClient = useSockClient();
  const submitHintLock = useUILock();
  const numUnusedHintTokens =
    numTotalHintTokens === null || numUsedHintTokens === null
      ? null
      : numTotalHintTokens - numUsedHintTokens;
  const openHintPuzName =
    teamHintsScopeState === null
      ? null
      : (Object.keys(teamHintsScopeState.openHints)[0] ?? null);
  const isHintOpenHere = teamHintsScopeState?.openHints[puzName] ?? false;
  const displayNames = usePuzzleDisplayNames();
  const isHuntOver = useIsHuntOver();
  const [reqInputLength, setReqInputLength] = useState(0);

  const canFollowup =
    numUnusedHintTokens !== null &&
    !isHintOpenHere &&
    sortedHints?.[0]?.[1].response?.type === HintResponseType.ANSWERED &&
    checkSubmitHint(
      isHuntOver ?? false,
      settings.isPosthunt,
      numUnusedHintTokens,
      openHintPuzName,
      true
    ).type === SubmitHintResultType.SUCCESS;

  useEffect(() => {
    if (!canFollowup) setIsFollowup(false);
  }, [canFollowup]);

  if (
    teamId === null ||
    sortedHints === null ||
    teamScopeState === null ||
    teamHintsScopeState === null ||
    //teamCannedHintsScopeState === null ||
    isSolved === null ||
    numUnusedHintTokens === null ||
    displayNames === null ||
    isHuntOver === null
  )
    return <Spinner inline={true} />;

  const hintError = ((): ReactNode | null => {
    if (numUnusedHintTokens === null) return null;
    if (isFollowup) return null;

    if (isHintOpenHere)
      return "You already have a hint pending on this puzzle!";
    const res = checkSubmitHint(
      isHuntOver,
      settings.isPosthunt,
      numUnusedHintTokens,
      openHintPuzName,
      false
    );
    switch (res.type) {
      case SubmitHintResultType.HUNT_IS_CLOSED:
        return "Sorry, hints are closed.";
      case SubmitHintResultType.NO_HINT_TOKENS:
        return "You have no hints available!";
      case SubmitHintResultType.OTHER_HINT_OPEN: {
        const { openHintPuzName } = res;
        return (
          <>
            You already have a hint pending on{" "}
            <PuzzleLink puzName={openHintPuzName} subpage="hints">
              {displayNames[openHintPuzName]}
            </PuzzleLink>
            ! You can have at most one hint pending at a time.
          </>
        );
      }
      case SubmitHintResultType.SUCCESS:
        return null;
    }
  })();

  const hintWord = numUnusedHintTokens === 1 ? "breadcrumb" : "breadcrumbs";
  const hintEmoji = <Twemoji tag="span">🍞️</Twemoji>;
  const hintCost = isFollowup ? 0 : 1;
  /*
  const cannedHints = teamCannedHintsScopeState.cannedHints[puzName] ?? {
    numTotal: 0,
    numOpened: 0,
  };
  const numMaxCannedHints = noCannedHints ? 0 : cannedHints.numMax;
  const areAllCannedHintsFound =
    numMaxCannedHints !== undefined &&
    cannedHints.numTotal >= numMaxCannedHints;
  */

  return (
    <>
      <h1>Hints</h1>
      <div>
        You have{" "}
        <b>
          {numUnusedHintTokens} {hintWord}
        </b>{" "}
        {hintEmoji} that you may use to ask for a hint on any puzzle. You may
        have <b>one hint</b> pending at a time; you must wait until that hint is
        answered to ask another.
      </div>
      <h4 ref={followupHintHeaderRef}>
        Request a {isFollowup ? "follow-up hint" : "hint"}
      </h4>
      {/*!areAllCannedHintsFound ? (
        <Paper seed="HintsDisplay: canned hints">
          There are {cannedHints.numTotal > 0 ? "more" : ""}{" "}
          <b>canned hints</b>{" "}
          <Icon icon={IconType.CANNED_HINT} className="hints-icon-disabled" />{" "}
          to be found for this puzzle, but you may still request a custom
          hint. If your hint is answered, you will also receive all{" "}
          {cannedHints.numTotal > 0 ? "remaining" : ""} canned hints for this
          puzzle.
        </Paper>
      ) : null*/}
      {isSolved ? (
        <Paper seed="HintsDisplay: already solved">
          You&rsquo;ve already solved this puzzle, but you can ask for a hint
          anyway if you&rsquo;d like (for example, if you&rsquo;re not sure
          about a step or you backsolved it). The restrictions above still
          apply.
        </Paper>
      ) : null}
      {isFollowup ? (
        <Paper seed="HintsDisplay: request followup">
          You&rsquo;re asking us for a follow-up to your last hint. This
          won&rsquo;t consume one of your hints; we&rsquo;ll see if we can be
          more helpful based on your submission, but we may ask you to use a new
          regular hint instead.
        </Paper>
      ) : null}
      <form
        onSubmit={(e: FormEvent<HTMLFormElement>) => {
          e.preventDefault();
          const { hintRequestContent, strength, shouldNotifyEmails } =
            Object.fromEntries(new FormData(e.currentTarget).entries());
          if (
            typeof hintRequestContent !== "string" ||
            !hintRequestContent.trim() ||
            typeof strength !== "string" ||
            !(Object.values(HintStrength) as string[]).includes(strength)
          ) {
            toastr.error("Please fill out the hint request.");
            return;
          }
          submitHintLock.acquireAndRun(async (getIsCanceled) => {
            await sockClient.sendScopeStepAndWaitAsync(
              {
                type: ScopeType.TEAM_HINTS,
                teamId,
              },
              {
                puzName,
                req: {
                  content: hintRequestContent,
                  isFollowup: isFollowup ? true : undefined,
                  shouldNotifyEmails: shouldNotifyEmails ? true : undefined,
                  strength: strength as HintStrength,
                },
              }
            );
          });
        }}
      >
        <label htmlFor="id_hint_question">
          Describe everything you&rsquo;ve tried on this puzzle. We will provide
          a hint to help you move forward. The more detail you provide, the less
          likely it is that we&rsquo;ll tell you something you already know. If
          you&rsquo;re working in a shared spreadsheet, it might help us if you
          linked it.
        </label>
        <textarea
          name="hintRequestContent"
          id="id_hint_question"
          ref={hintTextAreaRef}
          disabled={submitHintLock.isHeld}
          required
          onChange={() => {
            if (hintTextAreaRef.current === null) return;
            setReqInputLength(hintTextAreaRef.current.value.length);
          }}
        />
        {reqInputLength > HINT_CONFIG.requestMaxLength ? (
          <>
            <Paper seed="HintsDisplay: too long">
              Hint request too long ({reqInputLength} /{" "}
              {HINT_CONFIG.requestMaxLength} chars). If you have a lot of data
              to share, please link a spreadsheet or document instead. If
              necessary, don&rsquo;t forget to set permissions on the document
              so that anyone with the link can view it.
            </Paper>
          </>
        ) : null}
        <div className="hint-controls">
          <div className="hint-control">
            <Tooltip text={HINT_STRENGTH_TOOLTIP}>
              <Twemoji tag="span">ℹ️</Twemoji> How <strong>strong</strong> of a
              hint should we give?
            </Tooltip>
            {Object.values(HintStrength).map((strength) => (
              <Tooltip text={HINT_STRENGTH_DESC[strength]} key={strength}>
                <label>
                  <Twemoji tag="span">{HINT_STRENGTH_ICON[strength]}</Twemoji>{" "}
                  <input
                    name="strength"
                    type="radio"
                    disabled={submitHintLock.isHeld}
                    required
                    value={strength}
                  />
                </label>
              </Tooltip>
            ))}
          </div>
          <label className="hint-control">
            Send an email when the hint is answered?{" "}
            <input
              name="shouldNotifyEmails"
              type="checkbox"
              disabled={submitHintLock.isHeld}
              defaultChecked
            />
          </label>
        </div>
        {hintError !== null ? (
          isHintOpenHere ? null : (
            <Paper seed="HintsDisplay: error message">{hintError}</Paper>
          )
        ) : (
          <div>
            <button
              type="submit"
              disabled={
                submitHintLock.isHeld ||
                reqInputLength > HINT_CONFIG.requestMaxLength
              }
            >
              Submit (&minus;{hintCost} {hintEmoji})
            </button>
          </div>
        )}
      </form>
      {sortedHints.length > 0 ? (
        <>
          <h4>Previously requested hints</h4>
          {!isFollowup && canFollowup ? (
            <div className="hint-control">
              Did our hint not make sense or did we misunderstand your progress?
              <button
                onClick={() => {
                  setIsFollowup(true);
                  hintTextAreaRef.current?.focus();
                  followupHintHeaderRef.current?.scrollIntoView();
                  const strengthRadio = document.querySelector(
                    `input[name=strength][value=${sortedHints[0][1].request.strength}]`
                  );
                  if (strengthRadio)
                    (strengthRadio as HTMLInputElement).checked = true;
                }}
              >
                Request follow-up
              </button>
            </div>
          ) : null}
          {sortedHints.map(([hintId, hint]) => (
            <HintDisplay key={hintId} hintId={hintId} hint={hint} />
          ))}
          <Paper seed="HintsDisplay: all hints">
            You can see your hints from all puzzles on your{" "}
            <Link {...makeTeamPagePath(teamScopeState.displayName)}>
              team page
            </Link>
            .
          </Paper>
        </>
      ) : null}
    </>
  );
}

function HintsDisplay() {
  const puzData = useOutletContext<TeamPuzzleSockAPIScopeState>();
  const { slug } = puzData;
  const showHints = useShowHints(puzData);
  const setRedirectRoute = useNavStore((state) => state.setRedirectRoute);

  useEffect(() => {
    if (showHints ?? true) return;
    setRedirectRoute(
      slug ? { route: "puzzle", params: { slug } } : { route: "puzzles" }
    );
  }, [showHints, slug, setRedirectRoute]);

  if (!(showHints ?? false)) return <Spinner inline={true} />;

  return (
    <div className="puzzle-page-body hints-page">
      {/* <CannedHintsDisplay puzData={puzData} /> */}
      <CustomHintsDisplay puzData={puzData} />
    </div>
  );
}

export default HintsDisplay;
